import React, { useState, useEffect } from 'react';
import QRCodeCard from '../components/dashboard/QRCodeCard';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const { FiPlus, FiLoader, FiZap, FiShoppingBag, FiPlusCircle, FiCheck, FiX } = FiIcons;
import supabase from '../supabase/supabase';
import { fetchUserQRCodes, createQRCode, updateQRCodeDestination } from '../services/qrService';

// Stripe Price IDs (Replace with your actual Stripe Price IDs)
const PRICE_ID_1_CODE = "price_1TveasKy5UqrCTofg1Xwk6GH";
const PRICE_ID_5_CODES = "price_1TveapKy5UqrCTof9G2pnyTI";
const PRICE_ID_10_CODES = "price_1TveamKy5UqrCTof87pnCSNe";

const Dashboard = () => {
  const [codes, setCodes] = useState([]);
  const [maxCodes, setMaxCodes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [upgrading, setUpgrading] = useState(null); // Tracks which plan is being processed
  const [user, setUser] = useState(null);
  
  // Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newUrl, setNewUrl] = useState('');

  useEffect(() => {
    const checkUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      if (user) {
        await Promise.all([
          loadCodes(user.id),
          loadProfile(user.id)
        ]);
      } else {
        setLoading(false);
      }
    };
    checkUser();
  }, []);

  const loadCodes = async (userId) => {
    try {
      const data = await fetchUserQRCodes(userId);
      setCodes(data);
    } catch (error) {
      console.error('Failed to load codes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProfile = async (userId) => {
    try {
      const { data } = await supabase
        .from('profiles')
        .select('max_codes')
        .eq('id', userId)
        .single();
      if (data) setMaxCodes(data.max_codes);
    } catch (error) {
      console.error('Failed to load profile:', error);
    }
  };

  const formatAndValidateUrl = (input) => {
    if (!input) return null;
    let formatted = input.trim();
    if (!formatted) return null;
    if (!/^https?:\/\//i.test(formatted)) {
      formatted = `https://${formatted}`;
    }
    try {
      new URL(formatted);
      return formatted;
    } catch (e) {
      return null;
    }
  };

  const handleCreateNew = () => {
    if (!user) return alert('Please sign in to create QR codes');
    if (codes.length >= maxCodes) return alert('Quota reached! Please purchase more slots below.');
    setIsCreateModalOpen(true);
  };

  const handleSubmitCreate = async (e) => {
    if (e) e.preventDefault();
    
    const formattedUrl = formatAndValidateUrl(newUrl);
    if (!formattedUrl) {
      alert('Please enter a valid URL.');
      return;
    }

    setCreating(true);
    const { data, error } = await createQRCode(formattedUrl, user.id);
    setCreating(false);

    if (data) {
      setCodes([data, ...codes]);
      setIsCreateModalOpen(false);
      setNewUrl('');
    } else {
      alert(error || 'Failed to create QR code. Please try again.');
    }
  };

  const handleUpdateDestination = async (id, newUrl) => {
    try {
      await updateQRCodeDestination(id, newUrl);
      setCodes(codes.map(code => code.id === id ? { ...code, destination_url: newUrl } : code));
    } catch (error) {
      console.error('Update failed:', error);
      alert('Failed to update URL');
    }
  };

  const handleUpgrade = async (priceId, credits, planName) => {
    if (!user) return;
    setUpgrading(planName);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { 
          priceId, 
          userId: user.id, 
          email: user.email,
          credits 
        }
      });

      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Checkout error:', err);
      alert('Could not initiate checkout. Please contact support.');
    } finally {
      setUpgrading(null);
    }
  };

  const canCreate = codes.length < maxCodes;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f4f4f4]">
        <SafeIcon icon={FiLoader} className="w-12 h-12 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4f4f4] py-12 px-6 relative">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-6">
          <div>
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <h1 className="text-4xl font-black uppercase tracking-tighter text-left leading-none">Your Dashboard</h1>
              <div className="bg-black text-white px-3 py-1 text-xs font-black uppercase tracking-widest border-2 border-black">
                Quota: {codes.length} / {maxCodes}
              </div>
            </div>
            <p className="font-bold text-gray-600 text-left">Manage your permanent links and active slots.</p>
          </div>
          <button 
            onClick={handleCreateNew} 
            disabled={creating || !canCreate}
            className={`flex items-center gap-2 border-2 border-black px-6 py-3 font-black brutal-shadow-sm brutal-hover shrink-0 disabled:opacity-50 ${canCreate ? 'bg-black text-white' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`}
          >
            <SafeIcon icon={creating ? FiLoader : FiPlus} className={creating ? 'animate-spin' : ''} />
            {creating ? 'Creating...' : 'Create New Code'}
          </button>
        </div>

        <div className="mb-20">
          {codes.length === 0 ? (
            <div className="bg-white border-4 border-black p-12 text-center brutal-shadow">
              <h3 className="text-2xl font-black uppercase mb-4">No QR Codes Yet</h3>
              <p className="font-bold text-gray-600 mb-8">Ready to create your first eternal link?</p>
              {canCreate ? (
                <button onClick={handleCreateNew} className="bg-black text-white border-2 border-black px-8 py-4 font-black brutal-shadow brutal-hover">
                  Create Your First QR
                </button>
              ) : (
                <p className="text-red-600 font-black uppercase">Quota Reached. Purchase slots below.</p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {codes.map(code => (
                <QRCodeCard 
                  key={code.id} 
                  code={{ ...code, destinationUrl: code.destination_url, createdAt: new Date(code.created_at).toLocaleDateString() }} 
                  onUpdate={handleUpdateDestination}
                />
              ))}
            </div>
          )}
        </div>

        <div className="border-t-4 border-black pt-16">
          <div className="mb-10 text-center md:text-left">
            <h2 className="text-3xl font-black uppercase tracking-tight mb-2">Buy More Slots</h2>
            <p className="font-bold text-gray-600">Your QR codes are permanent. Purchase more slots to grow your collection.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Single Slot */}
            <div className="bg-white border-4 border-black p-6 brutal-shadow flex flex-col">
              <div className="flex justify-between items-start mb-4">
                <h3 className="font-black text-xl uppercase">Single</h3>
                <span className="bg-yellow-300 border-2 border-black px-2 py-0.5 text-xs font-black">$10</span>
              </div>
              <ul className="text-sm font-bold space-y-2 mb-8 flex-grow">
                <li className="flex items-center gap-2"><SafeIcon icon={FiCheck} className="text-green-600" /> +1 Dynamic QR</li>
                <li className="flex items-center gap-2"><SafeIcon icon={FiCheck} className="text-green-600" /> Lifetime Access</li>
              </ul>
              <button 
                onClick={() => handleUpgrade(PRICE_ID_1_CODE, 1, 'Single')}
                disabled={upgrading !== null}
                className="w-full bg-white text-black border-2 border-black py-3 font-black uppercase text-sm brutal-hover brutal-shadow-sm flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {upgrading === 'Single' ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiPlusCircle} />}
                Add 1 Slot
              </button>
            </div>

            {/* Standard Pack */}
            <div className="bg-black text-white border-4 border-black p-6 brutal-shadow-lg flex flex-col transform md:-translate-y-2">
              <div className="flex justify-between items-start mb-4">
                <h3 className="font-black text-xl uppercase">Standard</h3>
                <span className="bg-yellow-300 text-black border-2 border-black px-2 py-0.5 text-xs font-black">$40</span>
              </div>
              <ul className="text-sm font-bold space-y-2 mb-8 flex-grow">
                <li className="flex items-center gap-2"><SafeIcon icon={FiCheck} className="text-yellow-400" /> +5 Dynamic QRs</li>
                <li className="flex items-center gap-2"><SafeIcon icon={FiCheck} className="text-yellow-400" /> Best for Small Biz</li>
              </ul>
              <button 
                onClick={() => handleUpgrade(PRICE_ID_5_CODES, 5, 'Standard')}
                disabled={upgrading !== null}
                className="w-full bg-yellow-300 text-black border-2 border-black py-3 font-black uppercase text-sm brutal-hover brutal-shadow-sm flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {upgrading === 'Standard' ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiZap} />}
                Add 5 Slots
              </button>
            </div>

            {/* Pro Pack */}
            <div className="bg-white border-4 border-black p-6 brutal-shadow flex flex-col">
              <div className="flex justify-between items-start mb-4">
                <h3 className="font-black text-xl uppercase">Pro Pack</h3>
                <span className="bg-yellow-300 border-2 border-black px-2 py-0.5 text-xs font-black">$70</span>
              </div>
              <ul className="text-sm font-bold space-y-2 mb-8 flex-grow">
                <li className="flex items-center gap-2"><SafeIcon icon={FiCheck} className="text-green-600" /> +10 Dynamic QRs</li>
                <li className="flex items-center gap-2"><SafeIcon icon={FiCheck} className="text-green-600" /> Bulk Savings</li>
              </ul>
              <button 
                onClick={() => handleUpgrade(PRICE_ID_10_CODES, 10, 'Pro')}
                disabled={upgrading !== null}
                className="w-full bg-blue-600 text-white border-2 border-black py-3 font-black uppercase text-sm brutal-hover brutal-shadow-sm flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {upgrading === 'Pro' ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiShoppingBag} />}
                Add 10 Slots
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Create Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Overlay */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => !creating && setIsCreateModalOpen(false)}
          />
          
          {/* Modal Content */}
          <div className="relative bg-white border-4 border-black p-8 max-w-md w-full brutal-shadow-lg transform transition-all">
            <button 
              onClick={() => setIsCreateModalOpen(false)}
              className="absolute top-4 right-4 p-1 hover:bg-gray-100 border-2 border-transparent hover:border-black transition-all"
            >
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>

            <div className="mb-6">
              <h2 className="text-2xl font-black uppercase tracking-tight mb-2">New Eternal Link</h2>
              <p className="font-bold text-gray-600 text-sm">Enter the destination URL. You can change this later at any time.</p>
            </div>

            <form onSubmit={handleSubmitCreate}>
              <div className="mb-6">
                <label className="block text-xs font-black uppercase tracking-widest mb-2 text-gray-500">Destination URL</label>
                <input 
                  autoFocus
                  type="text"
                  placeholder="example.com or https://..."
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  className="w-full border-4 border-black p-4 font-bold focus:outline-none focus:bg-yellow-50 transition-colors"
                />
              </div>

              <div className="flex gap-4">
                <button 
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  disabled={creating}
                  className="flex-1 border-2 border-black py-3 font-black uppercase text-sm brutal-hover disabled:opacity-50"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={creating || !newUrl.trim()}
                  className="flex-1 bg-black text-white border-2 border-black py-3 font-black uppercase text-sm brutal-shadow-sm brutal-hover flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {creating ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiPlus} />}
                  {creating ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;